/**
 * 広告画像生成 一覧ページ。
 * 「+ 新規作成」で空 adImage を作成 → エディタへ即遷移。
 */
import { findAdSizePreset } from '@goenhub/shared';
import { Copy, CopyPlus, ImagePlus, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SlideThumbnail } from './components/SlideThumbnail';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useAdImages,
  useCreateEmptyAdImage,
  useDeleteAdImage,
  useDuplicateAdImage,
} from '@/hooks/useAdImages';
import { useAuth } from '@/hooks/useAuth';

function formatDate(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      return (d as { toDate(): Date }).toDate().toLocaleDateString('ja-JP');
    } catch {
      return '';
    }
  }
  return '';
}

function statusLabel(s: string): {
  label: string;
  variant: 'accent' | 'draft' | 'danger' | 'review';
} {
  if (s === 'ready') return { label: '生成済み', variant: 'accent' };
  if (s === 'generating') return { label: '生成中', variant: 'review' };
  if (s === 'failed') return { label: '失敗', variant: 'danger' };
  return { label: '下書き', variant: 'draft' };
}

export function AdImagesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useAdImages();
  const createEmpty = useCreateEmptyAdImage();
  const deleteAdImage = useDeleteAdImage();
  const duplicateAdImage = useDuplicateAdImage();
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const handleDuplicate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.uid) {
      toast.error('ログインが必要です');
      return;
    }
    setDuplicatingId(id);
    try {
      await duplicateAdImage.mutateAsync({ sourceId: id, ownerId: user.uid });
      toast.success('複製しました（一覧の先頭に追加されています）');
    } catch (err) {
      toast.error(`複製失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (id: string, title: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !confirm(
        `「${title}」を削除します。よろしいですか？\n（バリアントも個別に削除する必要があります）`,
      )
    )
      return;
    try {
      await deleteAdImage.mutateAsync(id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleCreate = async () => {
    if (!user?.uid) {
      toast.error('ログインが必要です');
      return;
    }
    try {
      const id = await createEmpty.mutateAsync({ ownerId: user.uid });
      navigate(`/ad-images/${id}`);
    } catch (e) {
      toast.error(`作成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Screen
      title="広告画像生成"
      description="SNS 各種のサイズに合わせた広告画像を AI で生成・編集"
      actions={
        <Button onClick={handleCreate} disabled={createEmpty.isPending}>
          {createEmpty.isPending ? (
            <Loader2 className="size-4 mr-1 animate-spin" />
          ) : (
            <Plus className="size-4 mr-1" />
          )}{' '}
          新規作成
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <ImagePlus className="size-12 text-[var(--text-muted)]" />
          <p className="text-[var(--text-muted)]">まだ広告画像がありません。</p>
          <Button onClick={handleCreate} disabled={createEmpty.isPending}>
            <Plus className="size-4 mr-1" /> 最初の広告画像を作成
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data?.map((a) => {
            const preset = findAdSizePreset(a.config.sizePresetId);
            const st = statusLabel(a.status);
            const firstSlide = a.slides?.[0];
            // Phase 41 改: 編集中のキャンバスと同じ DOM 構造で描画 (SlideThumbnail)。
            // 要素・背景がない場合は ImagePlus アイコンを表示。
            return (
              <div
                key={a.id}
                className="group relative text-left rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/ad-images/${a.id}`)}
                  className="w-full text-left"
                >
                  <div className="aspect-square bg-[var(--bg-root)] flex items-center justify-center overflow-hidden">
                    {firstSlide && preset ? (
                      <SlideThumbnail slide={firstSlide} preset={preset} displayWidth={360} />
                    ) : (
                      <ImagePlus className="size-10 text-[var(--text-muted)]" />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-sm truncate flex-1">{a.title}</h3>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {a.parentAdImageId && (
                          <Badge variant="review" title="マスターから生成されたバリアント">
                            <Copy className="size-3 mr-0.5" /> バリアント
                          </Badge>
                        )}
                        <Badge variant={st.variant}>{st.label}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-[var(--text-muted)] mb-1 truncate">
                      {preset ? preset.label : a.config.sizePresetId} ·{' '}
                      {a.config.audience === 'btob' ? 'BtoB' : 'BtoC'}
                    </p>
                    <div className="text-[11px] text-[var(--text-muted)]">
                      {a.slides?.length ?? 0} 枚 · 更新 {formatDate(a.updatedAt)}
                    </div>
                  </div>
                </button>
                <div className="absolute top-2 right-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => handleDuplicate(a.id, e)}
                    className={`p-1.5 rounded-md bg-black/60 text-white hover:bg-[var(--accent)] transition-opacity ${
                      duplicatingId === a.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}
                    title="複製"
                    aria-label={`「${a.title}」を複製`}
                    disabled={duplicatingId !== null}
                  >
                    {duplicatingId === a.id ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <CopyPlus className="size-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(a.id, a.title, e)}
                    className="p-1.5 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-[var(--accent-red)] transition-opacity"
                    title="削除"
                    aria-label={`「${a.title}」を削除`}
                    disabled={deleteAdImage.isPending}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
